/** `W-26` 결제의 **포인트 쓰기 칸**(목업 `W-26` · `W-26p`, 2026-10-25 둘째 회차 · O-89 (가) 2026-10-27).
 *
 * 셀러가 고르는 셋(`WebPointChoice`)과 그 두 몫(포인트 몫 · 결제 수단 몫)을 화면에 내고, 서버 셋을 부른다.
 *   - `GET  /api/points/balance`                 잔액. 칸을 세울지 정한다
 *   - `POST /api/points/pay/:jobId`              포인트로만
 *   - `POST /api/payments/confirm-with-points`   포인트 모두 쓰고 나머지는 결제 수단으로(토스 승인과 한 흐름)
 *
 * ## ⛔ 두 몫을 이 파일이 정하지 않는다
 *
 * 몫은 공유 `planWebPointSplit` 이 정하고, 서버는 승인 때 **같은 함수**(`webPointPlan`)로 다시 세어 맞댄다
 * (`apps/api/src/routes/payments.ts` ② · ③). 여기서 곱하거나 빼면 그 대조가 「화면이 만든 수와 화면이 만든 수」를
 * 비교하는 일이 된다. O-89 (가): 섞으면 **남은 포인트를 전부** 쓰고 나머지가 토스다. 작업 단위로 센다.
 *
 * ## ⛔ 스위치가 꺼져 있으면 칸이 서지 않는다
 *
 * 운영의 `POINTS_SPEND` 는 꺼져 있다. 결제 준비 응답(`GET /api/payments/checkout/:jobId`)은 스위치가 켜졌을 때만 `pointUse` 를
 * 싣는다. 그래서 칸은 `pointUse` 가 있고, 그 안에 지금 잔액으로 고를 수 있는 갈래(`pointUseFor`)가 있을 때만 선다. 누른 뒤
 * 꺼짐 거절(`POINTS_SPEND_OFF`)을 받으면 결제 준비를 다시 받는다. 다시 받은 응답에는 칸이 없다.
 *
 * ## ⛔ 「이 결제로 포인트 ₩X을 모두 씁니다…」의 숫자는 서버가 낸다 (법무 `L-F79`-7 · PRD `7.7.3a`)
 *
 * `pointUse` 는 서버가 SPEND 와 같은 꺼내는 순서로 센 것이다. 화면은 글자로 옮기기만 한다(`pointUseLine`). 그 줄 없이 칸을
 * 세우지 않는다. 서버의 몫과 화면의 몫(`planWebPointSplit`)이 어긋나면(그사이 잔액이 바뀜) 칸을 세우지 않는다.
 *
 * ⚠️ 응답 모양은 `@makit/shared` 에 아직 없다(서버가 `routes/points.ts` · `routes/payments.ts` 에 지역 타입으로 둠).
 *    아래 지역 타입은 그 모양을 그대로 옮긴 것이다. 공유 타입으로 오르면 이 타입들을 지운다(`data-manager` 소관).
 */

import {
  formatKrw,
  parseServerDate,
  planWebPointSplit,
  type PaymentPointSplit,
  type PointBalanceView,
  type WebPointChoice,
} from "@makit/shared";

import { ApiError, request, send } from "./api";

/* ── 서버 계약(지역 사본) ─────────────────────────────────────────────────── */

/** 결제 준비 응답의 `pointUse` 한 칸(`apps/api/src/lib/points.ts` `PointUsePreview`). ⚠️ `@makit/shared` 에 아직 없다. */
export interface PointUsePreview {
  choice: "POINTS_AND_TOSS" | "POINTS_ONLY";
  /** 이 결제가 쓰는 포인트. */
  pointAmount: number;
  /** 그중 현금 환불 창구 안 몫. 마감일이 이른 것부터. 없으면 빈 배열. */
  refundable: { amount: number; refundableUntil: string }[];
}

/** `GET /api/points/balance` (`routes/points.ts` `PointBalanceResponse`). 묶음(`packs`)은 이 화면이 읽지 않는다. */
interface PointBalanceResponse {
  balance: PointBalanceView;
}

/** `POST /api/points/pay/:jobId` 의 결과(`routes/points.ts` `PointPayResult`). */
export interface PointPayResult {
  jobId: string;
  paymentId: string;
  reused: boolean;
  balance: PointBalanceView;
}

/** `POST /api/payments/confirm-with-points` 본문. `amount` 는 **토스 몫**이다(합계가 아니다). */
export interface ConfirmWithPointsRequest {
  paymentKey: string;
  orderId: string;
  amount: number;
  pointAmount: number;
}

/** 그 결과(`routes/payments.ts` `ConfirmWithPointsResult`). `amount` 는 토스가 승인한 몫이다. */
export interface ConfirmWithPointsResult {
  paymentId: string;
  jobId: string;
  amount: number;
  methodLabel: string | null;
  reused: boolean;
  pointAmount: number;
}

/** 섞어 내기 승인의 실패. **`cardReverted` 를 함께 든다.** 서버는 토스 승인 **뒤** SPEND 가 안 섰을 때만 이 칸을 싣는다:
 *  `true` = 카드 몫을 되돌렸다(돈이 안 움직였다), `false` = 되돌렸는지 모른다(처리 · 새 결제는 표지가 막는다).
 *  `null` = 칸이 없다 = **토스를 부르기 전**의 거절이다. */
export class PointsConfirmError extends ApiError {
  readonly cardReverted: boolean | null;
  /** 이 승인에서 결제창이 받은 토스 몫. 되돌림 문장(`승인된 카드 결제 ₩N…`)의 금액이다. 서버 응답이 아니라 보낸 요청의 값이다. */
  readonly tossAmount: number;
  constructor(status: number, code: string | undefined, cardReverted: boolean | null, tossAmount: number) {
    super(status, code);
    this.name = "PointsConfirmError";
    this.cardReverted = cardReverted;
    this.tossAmount = tossAmount;
  }
}

/* ── 부르기 ───────────────────────────────────────────────────────────────── */

/** 잔액을 기다리는 상한. 넘기면 칸 없이 결제 화면을 세운다. ⛔ 늘리지 마라. 그동안 셀러는 「금액을 불러오는 중」을 본다. */
export const POINTS_BALANCE_TIMEOUT_MS = 3_000;

/** 이 화면이 포인트 칸을 세울 재료. **실패하거나 늦으면 `null`(칸을 세우지 않는다).**
 *
 * ⛔ 잔액을 못 읽었다고 결제를 막지 않는다. 포인트는 고를 수 있는 것이지 결제의 조건이 아니다. 401 은 `request` 가
 * 이미 세션 게이트를 깨운다. 늦은 잔액은 `POINTS_BALANCE_TIMEOUT_MS` 에서 끊는다(요청도 취소한다). */
export async function fetchPointsForPay(timeoutMs: number = POINTS_BALANCE_TIMEOUT_MS): Promise<PointBalanceView | null> {
  const abort = new AbortController();
  return withTimeout(
    request<PointBalanceResponse>("/api/points/balance", { signal: abort.signal }).then(
      (res) => res.balance ?? null,
      () => null,
    ),
    timeoutMs,
    () => {
      abort.abort();
      return null;
    },
  );
}

/** 포인트로만 낸다. 금액은 서버가 그 작업에서 다시 센다(본문에 금액이 없다). */
export function payJobWithPoints(jobId: string): Promise<PointPayResult> {
  return request<PointPayResult>(`/api/points/pay/${encodeURIComponent(jobId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ channel: "WEB" }),
  });
}

/** 섞어 낸 결제를 확정한다. 실패면 `PointsConfirmError`(코드와 `cardReverted`). 응답을 못 받으면 `ApiError(0)`. */
export async function confirmPaymentWithPoints(body: ConfirmWithPointsRequest): Promise<ConfirmWithPointsResult> {
  const res = await send("/api/payments/confirm-with-points", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.ok) return (await res.json()) as ConfirmWithPointsResult;
  // 본문은 한 번만 읽힌다. `readErrorCode` 와 같은 규율(코드 없는 5xx · 엣지 HTML 이면 코드가 없다)로 둘을 함께 꺼낸다.
  let parsed: { code?: unknown; cardReverted?: unknown } | null = null;
  try {
    parsed = (await res.json()) as { code?: unknown; cardReverted?: unknown };
  } catch {
    parsed = null;
  }
  const code = typeof parsed?.code === "string" ? parsed.code : undefined;
  const cardReverted = typeof parsed?.cardReverted === "boolean" ? parsed.cardReverted : null;
  throw new PointsConfirmError(res.status, code, cardReverted, body.amount);
}

/* ── 판정 시작 (points-pay-rules) ────────────────────────────────────────────
 *
 * ⛔ 이 구역은 `@makit/shared` 의 `planWebPointSplit` · `formatKrw` · `parseServerDate` 말고 바깥 것을 쓰지 않는다. `scripts/web-points-pay.test.mjs` 가 이 파일을 실제로
 * 불러 이 함수들을 부른다. */

/** 결제창 성공 주소에 싣는 포인트 몫의 이름. 결제창은 전체 이동이라 돌아올 때 이 값이 있어야 섞어 내기로 확정한다. */
export const RETURN_POINT_AMOUNT_PARAM = "pointAmount";

/** 칸을 세우는가. 잔액이 0 보다 크고 서버가 「지금 쓸 수 있다」고 한 셀러에게만(목업 `W-26`: 잔액 `₩0` 이면 칸이 없다). */
export function pointsOffered(view: PointBalanceView | null): view is PointBalanceView {
  return view !== null && view.canSpend && view.balance > 0;
}

/** 칸의 한 줄. `split` 이 `null` 이면 지금 잔액으로 서지 않는 갈래다. */
export interface PointOption {
  choice: WebPointChoice;
  split: PaymentPointSplit | null;
  /** 포인트로만이 서지 않을 때 모자라는 금액(목업 「포인트가 ₩1,000 모자랍니다」). 그 밖은 `null`. */
  shortBy: number | null;
}

/** 목업 `W-26` 의 줄과 순서. ① 섞기(0 < 잔액 < 합계일 때만 줄이 선다) ② 결제 수단으로만 ③ 포인트로만(모자라면 꺼진 채 사유). */
export function pointOptions(total: number, balance: number): PointOption[] {
  const options: PointOption[] = [];
  const mixed = planWebPointSplit(total, balance, "POINTS_AND_TOSS");
  if (mixed) options.push({ choice: "POINTS_AND_TOSS", split: mixed, shortBy: null });
  options.push({ choice: "TOSS_ONLY", split: planWebPointSplit(total, balance, "TOSS_ONLY"), shortBy: null });
  const only = planWebPointSplit(total, balance, "POINTS_ONLY");
  options.push({ choice: "POINTS_ONLY", split: only, shortBy: only ? null : Math.max(0, total - balance) });
  return options;
}

/** 이번 결제의 두 몫. 칸이 없으면 결제 수단으로만이고, 칸이 있는데 아직 안 골랐으면 `null`(버튼이 꺼진다). */
export function planForPay(
  total: number,
  points: PointBalanceView | null,
  choice: WebPointChoice | null,
): PaymentPointSplit | null {
  if (!pointsOffered(points)) return planWebPointSplit(total, 0, "TOSS_ONLY");
  if (choice === null) return null;
  return planWebPointSplit(total, points.balance, choice);
}

/** 섞어 낼 때 결제창이 돌아올 주소. 포인트 몫을 실어 둔다(토스는 이 주소에 결제 키 · 주문번호 · 금액을 덧붙인다). */
export function withReturnPointAmount(url: string, pointAmount: number): string {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${RETURN_POINT_AMOUNT_PARAM}=${pointAmount}`;
}

/** 돌아온 주소의 포인트 몫. 양의 정수가 아니면 `null`(= 결제 수단으로만 낸 결제). */
export function readReturnPointAmount(search: URLSearchParams): number | null {
  const raw = search.get(RETURN_POINT_AMOUNT_PARAM);
  if (raw === null || !/^[1-9][0-9]*$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) ? n : null;
}

/** 포인트로만 내기가 실패했을 때 화면이 할 일.
 *   - `SPEND_OFF`: 스위치가 꺼졌다. 칸을 숨기고 금액을 다시 받는다
 *   - `RELOAD`: 잔액 · 계정이 그사이 바뀌었다(`POINTS_INSUFFICIENT` · `POINTS_DEBT` · `POINTS_ACCOUNT_NOT_ACTIVE` · `POINTS_BUSY`).
 *     돈은 안 움직였다. 잔액을 다시 받아 칸을 새 숫자로 세운다
 *   - `ALREADY_PAID`: 이미 결제된 작업이다
 *   - `MESSAGE`: 그 밖. 결제 문장 표(`payment-messages.ts`)의 문장을 보인다 */
export function pointsPayOutcome(code: string | null): "SPEND_OFF" | "RELOAD" | "ALREADY_PAID" | "MESSAGE" {
  switch (code) {
    case "POINTS_SPEND_OFF":
      return "SPEND_OFF";
    case "POINTS_INSUFFICIENT":
    case "POINTS_DEBT":
    case "POINTS_ACCOUNT_NOT_ACTIVE":
    case "POINTS_BUSY":
      return "RELOAD";
    case "PAYMENT_ALREADY_PAID":
      return "ALREADY_PAID";
    default:
      return "MESSAGE";
  }
}

/** 결제 준비 응답에서 `pointUse` 를 꺼낸다. 칸이 없으면(스위치 꺼짐 · 원장을 못 읽음 · 옛 서버) `null`. 모양이 틀린 칸은 버린다. */
export function checkoutPointUse(quote: unknown): PointUsePreview[] | null {
  const raw = (quote as { pointUse?: unknown } | null)?.pointUse;
  if (!Array.isArray(raw)) return null;
  const int = (v: unknown): v is number => typeof v === "number" && Number.isSafeInteger(v) && v > 0;
  const out: PointUsePreview[] = [];
  for (const u of raw as unknown[]) {
    const o = u as Partial<PointUsePreview> | null;
    if (!o || (o.choice !== "POINTS_AND_TOSS" && o.choice !== "POINTS_ONLY") || !int(o.pointAmount)) continue;
    if (!Array.isArray(o.refundable)) continue;
    const refundable = o.refundable.filter((r) => r && int(r.amount) && typeof r.refundableUntil === "string");
    if (refundable.length !== o.refundable.length) continue;
    out.push({ choice: o.choice, pointAmount: o.pointAmount, refundable });
  }
  return out;
}

/** 지금 잔액으로 고를 수 있는 포인트 갈래(섞기 또는 포인트로만. 둘이 함께 서지 않는다)에 맞는 서버 미리 보기.
 *  **없으면 칸을 세우지 않는다**(`L-F79`-7 의 줄 없이 고르게 하지 않는다). 서버의 몫이 화면의 몫과 다르면(그사이 잔액이 바뀜)
 *  그것도 없는 것으로 본다. */
export function pointUseFor(
  total: number,
  points: PointBalanceView | null,
  pointUse: readonly PointUsePreview[] | null,
): PointUsePreview | null {
  if (!pointsOffered(points) || pointUse === null) return null;
  for (const choice of ["POINTS_AND_TOSS", "POINTS_ONLY"] as const) {
    const split = planWebPointSplit(total, points.balance, choice);
    if (!split) continue;
    const hit = pointUse.find((u) => u.choice === choice);
    return hit && hit.pointAmount === split.pointAmount ? hit : null;
  }
  return null;
}

/** 서버 시각을 셀러 기기의 시간대로 `MM-DD`. 못 읽으면 `""`(`formatListTime` 과 같은 빈 문자열 계약). */
export function localMonthDay(iso: string): string {
  const d = parseServerDate(iso);
  if (!d) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 고르기 전 한 줄(목업 `W-26` · PRD `7.7.3a` · `7.5.9`).
 *  - `이 결제로 포인트 ₩3,000을 모두 씁니다. 그중 ₩2,000은 09-13까지 환불할 수 있는 포인트입니다.`
 *  - 마감일이 둘 이상이면 `그중 ₩2,000은 09-13까지, ₩1,000은 09-15까지 환불할 수 있는 포인트입니다.`
 *  - 환불할 수 있는 몫이 없으면 둘째 문장을 뺀다.
 * @param monthDay 서버 시각(ISO)을 `MM-DD` 로. 화면이 셀러의 시간대로 넘긴다. 못 읽으면 `""` 이고 그 몫은 빠지지 않고 줄 전체를 접는다. */
export function pointUseLine(use: PointUsePreview, monthDay: (iso: string) => string): string | null {
  const first = `이 결제로 포인트 ${formatKrw(use.pointAmount)}을 모두 씁니다.`;
  if (use.refundable.length === 0) return first;
  const parts: string[] = [];
  for (const r of use.refundable) {
    const md = monthDay(r.refundableUntil);
    // ⛔ 날짜 없는 「환불할 수 있는 포인트」는 쓰지 않는 말이다(`7.5.9`). 날짜를 못 읽으면 줄을 세우지 않는다.
    if (!md) return null;
    parts.push(`${formatKrw(r.amount)}은 ${md}까지`);
  }
  return `${first} 그중 ${parts.join(", ")} 환불할 수 있는 포인트입니다.`;
}

/** `work` 를 기다리되 `ms` 가 지나면 `onTimeout()` 의 값으로 끝낸다. 늦게 끝난 `work` 는 버린다. */
export function withTimeout<T>(work: Promise<T>, ms: number, onTimeout: () => T): Promise<T> {
  return new Promise<T>((resolve) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      resolve(onTimeout());
    }, ms);
    const settle = (value: () => T) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(value());
    };
    // 던지는 `work` 도 화면을 막지 않는다. 늦은 것과 같이 `onTimeout` 의 값으로 끝낸다.
    work.then(
      (value) => settle(() => value),
      () => settle(onTimeout),
    );
  });
}

/* ── 판정 끝 (points-pay-rules) ──────────────────────────────────────────── */
